import * as React from "react";
import * as ReactDOM from "react-dom";
import Box from "@mui/material/Box";
import { useHotkeys } from "react-hotkeys-hook";
import Draggable from "react-draggable";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SxProps, Theme, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

interface PanelProps {
  title?: string;
  children: React.ReactNode;
  titleJsx?: React.ReactNode;
  onClose?: () => void;
  footer?: React.ReactNode;
  deleteButton?: React.ReactNode;
  editButton?: React.ReactNode;
  printButton?: React.ReactNode;
  genericButton?: React.ReactNode;
  messageButton?: React.ReactNode;
  wrapperStyle?: Record<string, unknown>;
  contentStyle?: SxProps<Theme>;
  footerStyle?: SxProps<Theme>;
}

const Panel: React.FC<PanelProps> = ({
  title,
  titleJsx,
  onClose,
  children,
  footer,
  deleteButton,
  editButton,
  printButton,
  messageButton,
  genericButton,
  wrapperStyle,
  contentStyle,
  footerStyle
}) => {
  const defaultPosition = { x: 0, y: 0 };
  const [position, setPosition] = React.useState(defaultPosition);
  const matchesSMScreen = useMediaQuery("(max-width:840px)");

  useHotkeys(
    "esc",
    () => {
      if (onClose) {
        onClose();
      }
    },
    { enableOnTags: ["INPUT", "SELECT", "TEXTAREA"] }
  );

  const jsx = (
    <Draggable
      handle=".handle"
      defaultPosition={defaultPosition}
      position={position}
      onStop={(e, p) => {
        setPosition({ x: p.x, y: 0 });
      }}
      grid={[25, 25]}
      scale={1}
    >
      <Box
        style={wrapperStyle}
        sx={{
          zIndex: 1201,
          minWidth: matchesSMScreen ? "100%" : "740px",
          maxWidth: matchesSMScreen ? "100%" : "740px",
          width: matchesSMScreen ? "100%" : undefined,
          background: "white",
          borderLeft: "1px solid grey",
          height: "100vh",
          position: "absolute",
          top: 0,
          right: 0,
          boxShadow: "0 -1px 50px 10px #00000057",
          overflowY: "auto"
        }}
        id="portalPanel"
      >
        {/* header */}
        <Box
          sx={{
            height: "50px",
            px: 4,
            display: "flex",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "lightgray"
          }}
        >
          {onClose && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center"
              }}
            >
              <Tooltip arrow title="Esc">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  data-testmation="panelCloseIcon"
                  size="small"
                  sx={{
                    mr: 1,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "10px"
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              alignItems: "center",
              cursor: "all-scroll"
            }}
          >
            {title ? (
              <Typography
                component="span"
                sx={{
                  height: "100%",
                  fontSize: "18px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {title}
              </Typography>
            ) : (
              titleJsx
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {genericButton && (
              <Box
                data-testmation="genericButton"
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {genericButton}
              </Box>
            )}
            {messageButton && (
              <Box
                data-testmation="messageButton"
                sx={{ height: "100%", display: "flex", alignItems: "center" }}
              >
                {messageButton}
              </Box>
            )}
            {printButton && (
              <Box
                data-testmation="printButton"
                sx={{ height: "100%", display: "flex", alignItems: "center" }}
              >
                {printButton}
              </Box>
            )}
            {deleteButton && (
              <Box
                data-testmation="deleteButton"
                sx={{ height: "100%", display: "flex", alignItems: "center" }}
              >
                {deleteButton}
              </Box>
            )}
            {editButton && (
              <Box
                data-testmation="editButton"
                sx={{ height: "100%", display: "flex", alignItems: "center" }}
              >
                {editButton}
              </Box>
            )}
          </Box>
        </Box>

        {/* main children */}
        <Box
          sx={{
            height: "calc(100vh - 100px)",
            width: "100%",
            px: 4,
            py: 2,
            overflowY: "auto",
            ...contentStyle
          }}
        >
          {children}
        </Box>

        {/* footer */}
        {footer && (
          <Box
            sx={{
              height: "50px",
              px: 4,
              display: "flex",
              borderTop: 1,
              borderColor: "divider",
              ...footerStyle
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Draggable>
  );

  return ReactDOM.createPortal(jsx, document.getElementById("portal"));
};

export default Panel;
